import React, { useContext, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const Login = () => {
	const navigate = useNavigate();
	const { login } = useContext(AuthContext);
	const [formData, setFormData] = useState({
		email: '',
		password: '',
	});
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const [rememberMe, setRememberMe] = useState(false);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({ ...prev, [name]: value }));
		setError('');
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		
		console.log('Login attempt with:', { email: formData.email, password: '***' });
		
		setIsLoading(true);
		try {
			const result = await login({
				username: formData.email, // Backend accepts both username and email
				password: formData.password,
			});

			console.log('Login result:', result);

			if (result.success) {
				// Navigate based on user role
				const userRole = result.user?.role;
				if (userRole === 'admin' || userRole === 'staff') {
					navigate('/admin/dashboard');
				} else {
					navigate('/');
				}
			} else {
				setError(result.message || 'Login failed. Please check your credentials.');
			}
		} catch (err) {
			console.error('Login error:', err);
			setError('An unexpected error occurred. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
			<div className="w-full max-w-md space-y-8">
				{/* Logo and heading */}
				<div className="text-center">
					<img
						src="/images/casa-gatchalian-logo.jpg"
						alt="Casa Gatchalian"
						className="h-16 w-16 rounded-full mx-auto mb-4 shadow-md"
					/>
					<h2 className="mt-6 text-3xl font-bold text-gray-900">Welcome Back</h2>
					<p className="mt-2 text-sm text-gray-600">Log in to your account</p>
				</div>

				{/* Error message */}
				{error && (
					<div className="rounded-md bg-red-50 p-4 border border-red-200">
						<div className="flex">
							<svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
								<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
							</svg>
							<div className="ml-3">
								<p className="text-sm text-red-800">{error}</p>
							</div>
						</div>
					</div>
				)}

				{/* Form */}
				<form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 rounded-lg shadow-md">
					{/* Email */}
					<div>
						<label htmlFor="email" className="block text-sm font-medium text-gray-700">Email or Username</label>
						<input
							type="text"
							name="email"
							id="email"
							placeholder="Enter your email or username"
							value={formData.email}
							onChange={handleChange}
							required
							className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0f4c81] focus:border-transparent text-sm"
						/>
					</div>

					{/* Password */}
					<div>
						<div className="flex items-center justify-between">
							<label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
							<Link to="/forgot-password" className="text-sm text-red-500 hover:underline">Forgot password?</Link>
						</div>
						<input
							type="password"
							name="password"
							id="password"
							placeholder="Enter your password"
							value={formData.password}
							onChange={handleChange}
							required
							className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0f4c81] focus:border-transparent text-sm"
						/>
					</div>

					{/* Remember me checkbox */}
					<div className="flex items-center">
						<input
							type="checkbox"
							name="rememberMe"
							id="rememberMe"
							checked={rememberMe}
							onChange={(e) => setRememberMe(e.target.checked)}
							className="h-4 w-4 text-[#0f4c81] focus:ring-[#0f4c81] border-gray-300 rounded"
						/>
						<label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700">
							Remember me
						</label>
					</div>

					{/* Log in button */}
					<button
						type="submit"
						disabled={isLoading}
						className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-[#0f4c81] hover:bg-[#0d3b66] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0f4c81]"
					>
						{isLoading ? 'Logging in...' : 'Log In'}
					</button>
				</form>

				{/* Sign up link */}
				<div className="text-center">
					<span className="text-sm text-gray-600">Don't have an account? </span>
					<button
						type="button"
						onClick={() => navigate('/signup')}
						className="font-medium text-[#0f4c81] hover:underline"
					>
						Sign up
					</button>
				</div>
			</div>
		</div>
	);
};

export default Login;
